/**
 * Where a host keeps the Record (D241) — the seam, and the one store every
 * shell can hand in: a key-value store, keyed per game, that never throws.
 *
 * @remarks
 * The engine never stores a Record: a run is handed one at genesis and
 * writes its layers into its own view (`engine-runtime` `record-of-run.ts`).
 * A host keeps it between runs, through this adapter — exactly as it keeps
 * saves through `SaveStorageAdapter`, and apart from them, so deleting a save
 * or starting over leaves the Record standing.
 *
 * Who holds it is the shell's to decide (Mike's ruling: with the account
 * when the player is signed in, on the device when they are not). This
 * module has the device's half, {@link createKeyValueRecordStorage}, over
 * any `getItem`/`setItem`/`removeItem` store — `localStorage` in the browser.
 * Every read and write is in `try`, because storage can be blocked, full, or
 * missing (a private window, a preview): an unreadable Record reads as none,
 * and one that cannot be written loses that write, never the run. The worst case is a
 * run that does not remember, which is a game, not a failure.
 *
 * @packageDocumentation
 */

import { mergeRecordLayers, type RecordLayer, recordLayersSchema } from '@orpheus/content-schema';

/** WHERE the Record lives — the shell supplies this; the hosts never know. */
export interface RecordStorageAdapter {
	/** Fold the layers a run wrote into this game's Record. */
	appendLayers(gameId: string, layers: readonly RecordLayer[]): Promise<void>;
	/** "Forget the Record" (Calder's ruling): delete this game's Record wherever it is held. */
	forgetRecord(gameId: string): Promise<void>;
	/** Read this game's Record back, if any — unknown-shaped: the hosts validate ({@link parsedRecordOf}). */
	readRecord(gameId: string): Promise<unknown>;
}

/** The sliver of `Storage` a device Record needs. */
export interface KeyValueStore {
	getItem(key: string): string | null;
	removeItem(key: string): void;
	setItem(key: string, value: string): void;
}

/** Where a device keeps one game's Record, beside — never inside — its saves. */
export const recordStorageKeyOf = (gameId: string): string => `orpheus.record.${gameId}`;

/**
 * A Record read from anywhere, validated: the shape, or none.
 *
 * @param raw - Whatever the storage handed back
 * @returns The layers, or an empty Record when they do not parse
 */
export const parsedRecordOf = (raw: unknown): readonly RecordLayer[] => {
	const parsed = recordLayersSchema.safeParse(raw);

	return parsed.success ? parsed.data : [];
};

/**
 * Read one game's Record through an adapter, never failing: an absent
 * adapter, a refused read and an unreadable Record all read as none.
 *
 * @param storage - The adapter, when the shell supplied one
 * @param gameId - The game
 * @returns The Record, or none
 */
export const readRecordSafely = async (storage: RecordStorageAdapter | undefined, gameId: string): Promise<readonly RecordLayer[]> => {
	if (storage === undefined) {
		return [];
	}

	try {
		return parsedRecordOf(await storage.readRecord(gameId));
	} catch {
		return [];
	}
};

/**
 * The device's Record store over a key-value store (`localStorage`).
 *
 * @param store - The key-value store; a getter, because merely touching `localStorage` can throw when storage is blocked
 * @returns The adapter
 */
export const createKeyValueRecordStorage = (store: () => KeyValueStore): RecordStorageAdapter => {
	/** Read one game's Record, or none — a missing, blocked or corrupt entry is none. */
	const read = (gameId: string): readonly RecordLayer[] => {
		try {
			const text = store().getItem(recordStorageKeyOf(gameId));

			return text === null ? [] : parsedRecordOf(JSON.parse(text));
		} catch {
			return [];
		}
	};

	return {
		appendLayers: async (gameId, layers) => {
			try {
				store().setItem(recordStorageKeyOf(gameId), JSON.stringify(mergeRecordLayers(read(gameId), layers)));
			} catch {
				// A full or blocked store loses this write; the run goes on.
			}

			return Promise.resolve();
		},
		forgetRecord: async (gameId) => {
			try {
				store().removeItem(recordStorageKeyOf(gameId));
			} catch {
				// Nothing could be read from a store that cannot be touched, so there is nothing left to forget.
			}

			return Promise.resolve();
		},
		readRecord: async (gameId) => Promise.resolve(read(gameId)),
	};
};
